/**
 * AdminRooms — Lista sale (Fase 2, top-level).
 * Route canonica: /admin/rooms
 * Ogni riga cliccabile → naviga a /admin/rooms/{id} (dettaglio + gestione tavoli).
 * Il click sulla riga NON interferisce con drag handle, toggle, archive,
 * rename inline (stopPropagation dove necessario).
 * Riuso @dnd-kit con TouchSensor + PointerSensor (touch-first).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { GripVertical, Archive, RotateCcw, Plus, Check, X, ChevronRight, LayoutGrid } from "lucide-react";
import {
  DndContext, PointerSensor, TouchSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";


function SortableRoomRow({ room, onRename, onToggleActive, onArchive, onRestore, onOpen }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(room.name);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: room.id, disabled: room.status === "archived" });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };

  const commit = async () => {
    const val = draft.trim();
    if (!val || val === room.name) { setEditing(false); setDraft(room.name); return; }
    await onRename(room.id, val);
    setEditing(false);
  };

  const archived = room.status === "archived";
  const stop = (e) => e.stopPropagation();

  return (
    <div ref={setNodeRef} style={style}
         data-testid={`room-row-${room.slug}`}
         onClick={() => !archived && !editing && onOpen(room.id)}
         className={`flex items-center gap-3 rounded-2xl border-2 px-3 py-3 sm:px-4 sm:py-3 bg-white
                    ${archived ? "opacity-60 border-sand-300 cursor-default"
                              : "border-sand-300 hover:border-bronze-500 cursor-pointer"}
                    transition-colors`}>
        {!archived && (
          <button
            type="button"
            data-testid={`room-drag-handle-${room.slug}`}
            {...attributes}
            {...listeners}
            onClick={stop}
            className="shrink-0 grid place-items-center h-11 w-11 rounded-lg text-ink-500
                       hover:bg-sand-100 active:bg-sand-200 cursor-grab touch-none select-none"
            aria-label={`Trascina per riordinare ${room.name}`}
          >
            <GripVertical className="w-5 h-5" strokeWidth={1.75} />
          </button>
        )}
        {archived && (
          <div className="shrink-0 grid place-items-center h-11 w-11 text-ink-400">
            <Archive className="w-5 h-5" strokeWidth={1.75} />
          </div>
        )}

        <div className="flex-1 min-w-0" onClick={editing ? stop : undefined}>
          {editing ? (
            <div className="flex gap-2 items-center" onClick={stop}>
              <Input autoFocus value={draft}
                     onChange={(e) => setDraft(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setEditing(false); setDraft(room.name); } }}
                     data-testid={`room-name-input-${room.slug}`}
                     className="h-11 font-sans" maxLength={60} />
              <Button size="icon" variant="ghost" className="h-11 w-11 shrink-0"
                      onClick={(e) => { stop(e); commit(); }}
                      data-testid={`room-name-save-${room.slug}`}>
                <Check className="w-5 h-5 text-emerald-700" />
              </Button>
              <Button size="icon" variant="ghost" className="h-11 w-11 shrink-0"
                      onClick={(e) => { stop(e); setEditing(false); setDraft(room.name); }}
                      data-testid={`room-name-cancel-${room.slug}`}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          ) : (
            <>
              <button type="button"
                      onClick={(e) => { stop(e); if (!archived) setEditing(true); }}
                      onDoubleClick={stop}
                      data-testid={`room-name-${room.slug}`}
                      disabled={archived}
                      className="text-left font-sans font-medium text-lg tracking-tight text-ink-900
                                 hover:text-bronze-700 disabled:cursor-default block truncate w-full">
                {room.name}
              </button>
              <div className="text-xs text-ink-500 mt-0.5 font-sans tabular-nums flex flex-wrap items-center gap-x-3">
                <span>slug: <span className="font-mono">{room.slug}</span></span>
                <span>{room.tables_count ?? 0} {(room.tables_count ?? 0) === 1 ? "tavolo" : "tavoli"}</span>
                {archived && <span className="text-red-700">archiviata</span>}
                {!archived && !room.is_active && <span className="text-amber-700">disattivata</span>}
              </div>
            </>
          )}
        </div>

        {!archived && (
          <>
            <div className="hidden sm:flex items-center gap-2 shrink-0" onClick={stop}>
              <span className="text-xs text-ink-500 font-sans">
                {room.is_active ? "Attiva" : "Disattivata"}
              </span>
              <Switch checked={!!room.is_active}
                      onCheckedChange={(v) => onToggleActive(room.id, v)}
                      data-testid={`room-active-toggle-${room.slug}`} />
            </div>
            <Button variant="ghost" size="icon"
                    className="h-11 w-11 shrink-0 text-red-600 hover:bg-red-50"
                    onClick={(e) => { stop(e); onArchive(room.id); }}
                    data-testid={`room-archive-${room.slug}`}
                    aria-label={`Archivia ${room.name}`}>
              <Archive className="w-5 h-5" />
            </Button>
            <ChevronRight className="w-5 h-5 text-ink-400 shrink-0" />
          </>
        )}
        {archived && (
          <Button variant="ghost" size="sm"
                  className="h-11 shrink-0 text-bronze-700"
                  onClick={(e) => { stop(e); onRestore(room.id); }}
                  data-testid={`room-restore-${room.slug}`}>
            <RotateCcw className="w-4 h-4 mr-1" /> Ripristina
          </Button>
        )}
    </div>
  );
}


export default function AdminRooms() {
  const nav = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor,   { activationConstraint: { delay: 250, tolerance: 8 } }),
  );

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/rooms?include_archived=${includeArchived}`);
      setRooms(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeArchived]);

  const active = useMemo(() => rooms.filter((r) => r.status !== "archived"), [rooms]);
  const archived = useMemo(() => rooms.filter((r) => r.status === "archived"), [rooms]);

  const onDragEnd = async (event) => {
    const { active: a, over } = event;
    if (!over || a.id === over.id) return;
    const oldIdx = active.findIndex((r) => r.id === a.id);
    const newIdx = active.findIndex((r) => r.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(active, oldIdx, newIdx);
    const items = reordered.map((r, i) => ({ id: r.id, display_order: (i + 1) * 10 }));
    setRooms((prev) => {
      const map = new Map(items.map((x) => [x.id, x.display_order]));
      return prev.map((r) => (map.has(r.id) ? { ...r, display_order: map.get(r.id) } : r))
                  .sort((x, y) => x.display_order - y.display_order);
    });
    try {
      await api.patch("/admin/rooms/reorder", { items });
      toast.success("Ordine sale aggiornato");
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const onRename = async (id, name) => {
    try { await api.patch(`/admin/rooms/${id}`, { name }); toast.success("Nome sala aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onToggleActive = async (id, is_active) => {
    try { await api.patch(`/admin/rooms/${id}`, { is_active });
          setRooms((prev) => prev.map((r) => r.id === id ? { ...r, is_active } : r)); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  };
  const onArchive = async (id) => {
    if (!window.confirm("Archiviare questa sala? Non sarà più visibile allo staff.")) return;
    try { await api.post(`/admin/rooms/${id}/archive`); toast.success("Sala archiviata"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onRestore = async (id) => {
    try { await api.post(`/admin/rooms/${id}/restore`); toast.success("Sala ripristinata"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onCreate = async () => {
    const n = newName.trim(); if (!n) return;
    setCreating(true);
    try { await api.post("/admin/rooms", { name: n }); toast.success(`Sala "${n}" creata`); setNewName(""); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };
  const onOpen = (id) => nav(`/admin/rooms/${id}`);

  return (
    <div className="max-w-3xl" data-testid="admin-rooms">
      <div className="mb-6">
        <div className="eyebrow">Configurazione</div>
        <h1 className="text-4xl mt-1">Sale</h1>
        <p className="text-sm text-ink-500 mt-2 max-w-xl font-sans">
          Aree operative della sala live. Tocca una sala per gestire i suoi tavoli.
          Trascina dalla maniglia <span className="inline-block align-middle"><GripVertical className="w-4 h-4 inline" /></span> per riordinare.
        </p>
      </div>

      <section className="card-elevated p-4 sm:p-5" data-testid="rooms-create-card">
        <div className="eyebrow mb-2">Nuova sala</div>
        <div className="flex gap-2 items-center">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") onCreate(); }}
                 placeholder="Es. TERRAZZA MARE" maxLength={60}
                 className="h-11 font-sans flex-1" data-testid="room-new-name-input" />
          <Button onClick={onCreate} disabled={creating || !newName.trim()}
                  className="h-11 rounded-full whitespace-nowrap"
                  data-testid="room-new-create-btn">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi
          </Button>
        </div>
        <p className="text-xs text-ink-500 mt-2 font-sans">
          Lo slug (identificatore tecnico) viene generato automaticamente e resterà invariato.
        </p>
      </section>

      <section className="mt-6" data-testid="rooms-list-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-sans font-semibold text-ink-900 flex items-center gap-2">
            <LayoutGrid className="w-4 h-4 text-ink-400" /> Sale ({active.length})
          </h2>
          <label className="flex items-center gap-2 text-sm font-sans cursor-pointer">
            <input type="checkbox" checked={includeArchived}
                   onChange={(e) => setIncludeArchived(e.target.checked)}
                   data-testid="rooms-toggle-include-archived"
                   className="h-4 w-4" />
            Mostra archiviate ({archived.length})
          </label>
        </div>

        {loading ? (
          <div className="text-ink-400 py-8 font-sans">Caricamento…</div>
        ) : active.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-sand-300 p-8 text-center text-ink-500 font-sans"
               data-testid="rooms-empty">
            Nessuna sala configurata.
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={active.map((r) => r.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {active.map((r) => (
                  <SortableRoomRow key={r.id} room={r}
                                    onRename={onRename}
                                    onToggleActive={onToggleActive}
                                    onArchive={onArchive}
                                    onRestore={onRestore}
                                    onOpen={onOpen} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </section>

      {includeArchived && archived.length > 0 && (
        <section className="mt-8" data-testid="rooms-archived-section">
          <h2 className="text-lg font-sans font-semibold text-ink-500 mb-3">
            Archiviate ({archived.length})
          </h2>
          <div className="space-y-2">
            {archived.map((r) => (
              <SortableRoomRow key={r.id} room={r}
                                onRename={onRename}
                                onToggleActive={onToggleActive}
                                onArchive={onArchive}
                                onRestore={onRestore}
                                onOpen={onOpen} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
